import { createAsyncThunk } from '@reduxjs/toolkit';
import { postRequest, getRequest, putRequest } from '../../../axios/requests';
import API from '../../../constant.api';
import { type PledgePayment } from './pledgeTypes';

export const createNewPledge = createAsyncThunk(
  'pledge/create',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await postRequest(API.PLEDGES, payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateObNumber = createAsyncThunk(
  'pledge/updateOb',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await putRequest(`${API.PLEDGES}/${payload.id}/update-obnumber`, {obNumber: payload.obNumber});
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateObTokenImage = createAsyncThunk(
  'pledge/updateObTokenImage',
  async (payload: { id: string | undefined; obTokenImage: string }, { rejectWithValue }) => {
    try {
      const res = await putRequest(`${API.PLEDGES}/${payload.id}/update-obtoken-image`, { obTokenImage: payload.obTokenImage });
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPledgeTypes = createAsyncThunk(
  'pledge/get',
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await getRequest(API.BANK_INTEREST, payload);
     
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPledgeById = createAsyncThunk(
  'pledge/pledgeById',
  async (payload: Record<string, string>, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGES}/list/search`,payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const pledgeRepayment = createAsyncThunk(
  'pledge/close',
  async (payload: PledgePayment, { rejectWithValue }) => {
    try {
      const { data } = await postRequest(
        `${API.PLEDGE_PAYMENTS}`, payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUnclosedPledge = createAsyncThunk(
  'pledge/unclosed',
  async (payload: {branch: string}, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGES}/list/unclosed`,payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getRenewalPledge = createAsyncThunk(
  'pledge/renewal',
  async (payload: {branch: string}, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGES}/list/renewal`,payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const splitJewel = createAsyncThunk(
  'jewels/split',
  async (payload: { loanNumber?: string, saleNumber?: string, jewelId: string, jewels: any}, { rejectWithValue }) => {
    try {
      // Use whichever number is provided
      const number = payload.loanNumber ?? payload.saleNumber;

      if (number === undefined || number === null || number === '') {
        throw new Error('Either loanNumber or saleNumber must be provided');
      }

      // Check the 4th character (index 3) to determine if it's a loan or sale
      const fourthChar = number.charAt(3);
      let apiEndpoint: string;

      if (fourthChar === 'L') {
        // Use Loan API
        apiEndpoint = `${API.LOANS}/jewel-split/${number}/${payload.jewelId}`;
      } else if (fourthChar === 'S') {
        // Use Sale API
        apiEndpoint = `${API.SALES}/jewel-split/${number}/${payload.jewelId}`;
      } else {
        throw new Error('Invalid number format: 4th character must be L or S');
      }

      const { data } = await postRequest(apiEndpoint, {jewels: payload.jewels});
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUnapprovedPledgePayment = createAsyncThunk(
  'pledge-payment/unapproved',
  async (payload: null, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGE_PAYMENTS}/list/approvals`, payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUnapprovedPledge = createAsyncThunk(
  'pledge/unapproved',
  async (payload: null, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGES}/list/approvals`, payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);


export const updatePledgePaymentSplit = createAsyncThunk(
  'pledge-payments/updateSplit',
  async (
    payload: {
      id: string;
      onlinePrincipal: number;
      offlinePrincipal: number;
      onlineInterest: number;
      offlineInterest: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await putRequest(
        `${API.PLEDGE_PAYMENTS}/split/${payload.id}`,
        {
          onlinePrincipal: payload.onlinePrincipal,
          offlinePrincipal: payload.offlinePrincipal,
          onlineInterest: payload.onlineInterest,
          offlineInterest: payload.offlineInterest,
        }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePledgePayment = createAsyncThunk(
  'loan/approval',
  async (payload: any, { rejectWithValue }) => {
    const payloadData = {status: payload.status}
    try {
      const res = await putRequest(`${API.PLEDGE_PAYMENTS}/${payload.id}`, payloadData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePledgeApproval = createAsyncThunk(
  'pledge/approval',
  async (payload: any, { rejectWithValue }) => {
    const payloadData = {status: payload.status}
    try {
      const res = await putRequest(`${API.PLEDGES}/${payload.id}`, payloadData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUnsettledPledgePayments = createAsyncThunk(
  'payments/unsettled',
  async (payload: null, { rejectWithValue }) => {
    try {
      const { data } = await getRequest(
        `${API.PLEDGE_PAYMENTS}/list/unsettled`, payload
      )
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);


